import React, { useEffect, useState } from 'react';
import { Trash2, CheckCircle2, Eye, X, MessageCircle, ArchiveX } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  reviewed: 'Revisado',
  archived: 'Archivado',
};

const STATUS_COLORS = {
  pending: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  reviewed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  archived: 'bg-white/10 text-white/40 border-white/10',
};

const PreOrdersPanel = () => {
  const [preOrders, setPreOrders] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const res = await api.get('/admin/pre-orders', { params });
      setPreOrders(res.data);
    } catch {
      toast.error('Error cargando pre-pedidos');
    } finally {
      setLoading(false);
    }
  };

  const counts = { all: 0, pending: 0, reviewed: 0, archived: 0 };

  const handleSetStatus = async (id, status) => {
    try {
      await api.patch(`/admin/pre-orders/${id}`, { status });
      toast.success(`Marcado como ${STATUS_LABELS[status].toLowerCase()}`);
      load();
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar definitivamente este pre-pedido?\n\nUsa esto cuando ya hayas creado la factura correspondiente.')) return;
    try {
      await api.delete(`/admin/pre-orders/${id}`);
      toast.success('Pre-pedido eliminado');
      load();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold mb-1">Pre-pedidos</h2>
          <p className="text-xs md:text-sm text-white/60">
            Pre-pedidos enviados por clientes desde el portal. <strong className="text-orange-400">No afectan inventario.</strong>
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        {['pending', 'reviewed', 'archived', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`preorder-filter-${f}`}
            className={`px-4 py-2.5 text-xs uppercase tracking-wider border transition-colors ${
              filter === f ? 'bg-white text-black border-white' : 'border-white/10 text-white/60 hover:border-white/30'
            }`}
          >
            {f === 'all' ? 'Todos' : STATUS_LABELS[f]}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-center py-12 text-white/40">Cargando...</p>
      ) : preOrders.length === 0 ? (
        <p className="text-center py-16 text-white/40 text-sm">No hay pre-pedidos {filter !== 'all' ? STATUS_LABELS[filter].toLowerCase() + 's' : ''}.</p>
      ) : (
        <div className="space-y-3">
          {preOrders.map((po) => (
            <div key={po.id} data-testid={`admin-preorder-${po.id}`} className="bg-[#0A0A0A] border border-white/10 p-4 hover:border-white/20 transition-colors">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold">{po.client_name}</span>
                    {po.client_code && (
                      <span className="text-xs font-mono tracking-widest px-2 py-0.5 bg-white/10 border border-white/20 rounded">
                        {po.client_code}
                      </span>
                    )}
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${STATUS_COLORS[po.status]}`}>
                      {STATUS_LABELS[po.status]}
                    </span>
                  </div>
                  <p className="text-xs text-white/40">{formatDate(po.created_at)} · {po.items.length} modelo(s)</p>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <button onClick={() => setViewing(po)} data-testid={`view-preorder-${po.id}`} className="px-3 py-2 text-xs border border-white/10 hover:border-white/30 flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5" /> Ver
                  </button>
                  {po.status === 'pending' && (
                    <button onClick={() => handleSetStatus(po.id, 'reviewed')} className="px-3 py-2 text-xs border border-blue-500/40 text-blue-400 hover:bg-blue-500/10 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Marcar revisado
                    </button>
                  )}
                  {po.status !== 'archived' && (
                    <button onClick={() => handleSetStatus(po.id, 'archived')} className="px-3 py-2 text-xs border border-white/10 hover:border-white/30 flex items-center gap-1.5">
                      <ArchiveX className="w-3.5 h-3.5" /> Archivar
                    </button>
                  )}
                  <button onClick={() => handleDelete(po.id)} data-testid={`delete-preorder-${po.id}`} className="px-3 py-2 text-xs border border-red-500/40 text-red-400 hover:bg-red-500/10 flex items-center gap-1.5">
                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                  </button>
                </div>
              </div>

              {/* Preview thumbnails */}
              <div className="flex gap-2 overflow-x-auto pt-3 border-t border-white/5">
                {po.items.slice(0, 8).map((it, idx) => (
                  <div key={idx} className="flex-shrink-0 w-16 h-16 bg-[#121212] border border-white/5 overflow-hidden relative" title={it.product_name}>
                    {it.product_image && <img src={it.product_image} alt={it.product_name} className="w-full h-full object-cover" />}
                    {it.package_quantity > 1 && (
                      <span className="absolute bottom-0 right-0 bg-emerald-500 text-black text-[9px] font-bold px-1 leading-tight">×{it.package_quantity}</span>
                    )}
                  </div>
                ))}
                {po.items.length > 8 && (
                  <div className="flex-shrink-0 w-16 h-16 border border-white/10 flex items-center justify-center text-xs text-white/40">
                    +{po.items.length - 8}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {viewing && <PreOrderDetailDialog preOrder={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
};

const PreOrderDetailDialog = ({ preOrder, onClose }) => {
  const handleWhatsApp = () => {
    const list = preOrder.items.map((it, i) => `${i + 1}. ${it.product_name}${it.product_sku ? ` (${it.product_sku})` : ''} — ${it.package_quantity || 1} paquete(s)`).join('\n');
    const totalPkgs = preOrder.items.reduce((s, it) => s + (it.package_quantity || 1), 0);
    const msg = encodeURIComponent(`Hola ${preOrder.client_name}, recibí tu pre-pedido (${totalPkgs} paquetes en total):\n\n${list}\n\n¿Confirmamos tallas y piezas por paquete?`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold">Pre-pedido de {preOrder.client_name}</h3>
            <p className="text-xs text-white/50 mt-1">{formatDate(preOrder.created_at)} · Código {preOrder.client_code || '—'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        {preOrder.notes && (
          <div className="bg-yellow-500/5 border border-yellow-500/20 p-3 mb-5">
            <p className="text-[10px] uppercase tracking-wider text-yellow-400 mb-1">Notas del cliente</p>
            <p className="text-sm text-white/80 italic">"{preOrder.notes}"</p>
          </div>
        )}

        <p className="text-xs uppercase tracking-wider text-white/60 mb-3">
          {preOrder.items.length} modelo(s) seleccionados · {preOrder.items.reduce((s, it) => s + (it.package_quantity || 1), 0)} paquete(s) en total
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {preOrder.items.map((it, idx) => (
            <div key={idx} className="bg-[#121212] border border-white/5 relative">
              {it.product_image && <img src={it.product_image} alt={it.product_name} className="w-full aspect-square object-cover" />}
              <span className="absolute top-2 right-2 bg-emerald-500 text-black text-xs font-bold px-2 py-1 rounded">
                ×{it.package_quantity || 1}
              </span>
              <div className="p-2">
                <p className="text-xs truncate">{it.product_name}</p>
                {it.product_brand && <p className="text-[10px] text-white/40 truncate">{it.product_brand}</p>}
                {it.product_sku && <p className="text-[10px] text-white/30 mt-0.5">SKU: {it.product_sku}</p>}
                <p className="text-[10px] text-emerald-400 mt-1 font-semibold">{it.package_quantity || 1} paquete(s) solicitado(s)</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={handleWhatsApp} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-3 text-xs uppercase tracking-wider flex items-center justify-center gap-2">
            <MessageCircle className="w-4 h-4" /> Contactar por WhatsApp
          </button>
          <button onClick={onClose} className="flex-1 border border-white/20 hover:bg-white/5 px-4 py-3 text-xs uppercase tracking-wider">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreOrdersPanel;
